import { Link } from "react-router-dom";
import { Facebook, Instagram, Star, ArrowUpRight } from "lucide-react";
import { nav, school, logo, googleReviews } from "../mock/mock";
import ReceptionStatus from "./ReceptionStatus";
import SwishLogo from "./SwishLogo";

export default function Footer() {
  return (
    <footer className="relative bg-[#071a33] text-slate-300 overflow-hidden" data-testid="footer">
      <div className="h-1 bg-gradient-to-r from-[#ffce3a] via-[#ffd85f] to-[#ffce3a]" />
      <div className="absolute -top-40 right-0 w-[36rem] h-[36rem] rounded-full bg-[#1B6CA8]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 pt-16 pb-10 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <img src={logo} alt="City Trafikskola Uppsala" className="h-16 w-auto invert" data-testid="footer-logo" />
          <p className="text-sm leading-relaxed text-slate-400 mt-5 max-w-sm">
            {school.legalName}. Komplett körkortsutbildning för B-behörighet i fräscha lokaler vid {school.area} – teori på sex språk, moderna bilar och digital bokning dygnet runt.
          </p>
          <a href={googleReviews.url} target="_blank" rel="noopener noreferrer" data-testid="footer-google-rating" className="group mt-6 inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
            <span className="font-display text-3xl font-black text-[#ffce3a] leading-none">{googleReviews.rating}</span>
            <span>
              <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="text-[#ffce3a] fill-[#ffce3a]" />)}</span>
              <span className="block text-xs text-slate-400 mt-0.5">{googleReviews.count} omdömen på Google</span>
            </span>
            <ArrowUpRight size={16} className="text-slate-500 group-hover:text-[#ffce3a] transition-colors" />
          </a>
          <div className="flex gap-3 mt-6">
            <a href={school.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffce3a] hover:text-[#0B2545] hover:border-[#ffce3a] transition-colors">
              <Facebook size={18} />
            </a>
            <a href={school.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#ffce3a] hover:text-[#0B2545] hover:border-[#ffce3a] transition-colors">
              <Instagram size={18} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-display font-bold mb-5 text-sm uppercase tracking-[0.15em]">Snabblänkar</h4>
          <ul className="space-y-3 text-sm">
            {nav.map((item) => (
              <li key={item.path}>
                <Link to={item.path} className="group inline-flex items-center gap-2 hover:text-[#ffce3a] transition-colors">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ffce3a]/40 group-hover:bg-[#ffce3a] transition-colors" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white font-display font-bold mb-5 text-sm uppercase tracking-[0.15em]">Kontakt</h4>
          <ul className="space-y-4 text-sm">
            <li>
              <a href={`mailto:${school.email}`} className="group flex items-center gap-3 hover:text-[#ffce3a] transition-colors">
                <span className="break-all">{school.email}</span>
              </a>
            </li>
            <li>
              <a href="tel:+46182650646" className="group flex items-center gap-3 hover:text-[#ffce3a] transition-colors">
                018-265 06 46
              </a>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex items-center gap-2"><SwishLogo className="h-5" dark /> <span data-testid="footer-swish-number">{school.swish}</span></span>
            </li>
            <li className="flex items-center gap-3">
              {school.address}, {school.postal}
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white font-display font-bold mb-5 text-sm uppercase tracking-[0.15em]">{school.hoursTitle}</h4>
          <ul className="text-sm divide-y divide-white/5 rounded-xl bg-white/5 border border-white/10 overflow-hidden">
            {school.hours.map((h) => (
              <li key={h.day} className="flex items-center justify-between gap-3 px-4 py-2">
                <span className="text-slate-400">{h.day}</span>
                <span className={`text-right ${h.time.startsWith("Enligt") ? "text-slate-400 text-xs" : "text-white font-semibold"}`}>{h.time}</span>
              </li>
            ))}
          </ul>
          <ReceptionStatus dark />
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {school.legalName}. Alla rättigheter förbehållna.</p>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-center">
            <span>Org.nr 559426-4870</span>
            <Link to="/integritetspolicy" className="hover:text-[#ffce3a] transition-colors underline-offset-2 hover:underline" data-testid="footer-privacy-link">Integritetspolicy & cookies</Link>
            <button type="button" onClick={() => window.dispatchEvent(new Event("ct-open-cookie-settings"))} className="hover:text-[#ffce3a] transition-colors underline-offset-2 hover:underline" data-testid="footer-cookie-settings-btn">Cookie-inställningar</button>
          </p>
        </div>
      </div>
    </footer>
  );
}
